<template>
  <div class="container">
    <main>
      <section class="hero">
        <div class="hero-content">
          <h1>Precision image-guided surgery</h1>
          <p class="hero-sub">We believe more patients can access minimally invasive care. We're building the system to make it possible.</p>
          <div class="hero-btns">
            <router-link to="/technology" class="btn btn-primary">How it works</router-link>
            <router-link to="/contact" class="btn btn-secondary">Contact us</router-link>
          </div>
        </div>
      </section>

      <section class="partners-section">
        <div class="partners-inner">
          <p class="partners-headline">Funders and partners</p>
          <p class="partners-legal">Grant funding and program participation—not an endorsement.</p>
          <div class="partners-logos" aria-label="Funders and partners">
            <div class="partner-logo-wrap"><img src="@/assets/logo-cprit.svg" alt="Cancer Prevention Research Institute of Texas" class="partner-logo" /></div>
            <div class="partner-logo-wrap"><img src="@/assets/ucsf-rosenman-logo.jpg" alt="UCSF Rosenman Institute" class="partner-logo" /></div>
            <div class="partner-logo-wrap"><img src="@/assets/institute-for-the-global-entrepreneur.png" alt="Institute for the Global Entrepreneur" class="partner-logo" /></div>
            <div class="partner-logo-wrap"><img src="@/assets/ucsd-sullivan-center.png" alt="Sullivan Center for Entrepreneurship, Rady School of Management" class="partner-logo" /></div>
            <div class="partner-logo-wrap"><img src="@/assets/nsf-logo.png" alt="National Science Foundation" class="partner-logo" /></div>
          </div>
        </div>
      </section>

      <section class="problem-band">
        <div class="problem-inner">
          <p>Each year, over 300,000 patients could benefit from minimally invasive needle-based treatment. Today, only a fraction are treated this way—often because accuracy and workflow are hard to achieve.</p>
        </div>
      </section>

      <section class="system-section">
        <div class="system-inner">
          <div class="system-visual">
            <img src="@/assets/product-system.gif" alt="Air Surgical in-scanner robotic system" class="system-image" />
          </div>
          <div class="system-copy">
            <h2>The system physicians need</h2>
            <p>An in-scanner robotic system with closed-loop image guidance. High accuracy in preclinical studies. Short setup. Intuitive workflow—so you focus on the patient, not the hardware.</p>
            <router-link to="/technology" class="link-arrow">Learn more <span class="link-arrow-icon" aria-hidden="true">→</span></router-link>
          </div>
        </div>
      </section>

      <section class="impact-section">
        <div class="impact-inner">
          <h2>When performed accurately, minimally invasive treatment can mean</h2>
          <ul class="impact-list">
            <li>Shorter recovery</li>
            <li>Lower procedure cost</li>
            <li>Lower mortality and complication rates</li>
            <li>Comparable efficacy and recurrence rates</li>
          </ul>
          <p class="impact-close">We’re building the system so more patients can access this level of care.</p>
        </div>
      </section>

      <section class="cta-section">
        <div class="cta-inner">
          <router-link to="/technology" class="btn btn-primary">Explore technology</router-link>
          <router-link to="/contact" class="btn btn-outline">Get in touch</router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  mounted() {
    document.title = 'Air Surgical | Precision Cancer Care';
  }
}
</script>

<style scoped>
.container {
  margin: 0;
  padding: 0;
  padding-top: 70px;
}

.main {
  text-align: center;
}

.hero {
  background-image: url('@/assets/robotic-instrument-positioning.png');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: #1a2d42;
  color: #fff;
  min-height: 70vh;
  padding: 120px 24px 100px;
  width: 100vw;
  margin: 0;
  margin-left: calc(-50vw + 50%);
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .hero {
    max-height: 55vh;
    min-height: 50vh;
    padding: 80px 24px 60px;
  }
}

@media (max-width: 767px) and (orientation: landscape) {
  .hero {
    max-height: none;
    min-height: auto;
    padding: 32px 24px 40px;
  }
}

.partners-section {
  padding: 48px 24px;
  background: #fff;
  text-align: center;
}

.partners-inner {
  max-width: 1000px;
  margin: 0 auto;
}

.partners-headline {
  font-size: 1rem;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.5rem 0;
}

.partners-legal {
  font-size: 0.875rem;
  color: #475569;
  margin: 0 0 2rem 0;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.partners-logos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 2rem 2.5rem;
}

.partners-section .partner-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
}

.partners-section .partner-logo {
  height: 52px;
  width: auto;
  max-width: 170px;
  object-fit: contain;
  display: block;
}

@media (min-width: 640px) {
  .partners-section .partner-logo {
    height: 60px;
    max-width: 200px;
  }
}

.problem-band {
  background: #1e3a5f;
  color: #fff;
  padding: 40px 24px;
  text-align: center;
}

.problem-inner {
  max-width: 720px;
  margin: 0 auto;
}

.problem-inner p {
  font-size: 1.25rem;
  line-height: 1.6;
  margin: 0;
}

.system-section {
  padding: 44px 24px 36px;
  background: #fff;
}

.system-inner {
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  gap: 16px;
  align-items: center;
}

@media (max-width: 767px) {
  .problem-band {
    padding: 28px 20px 28px;
  }
  .system-section {
    padding: 20px 20px 28px;
  }
  .system-inner {
    gap: 8px;
  }
}

@media (min-width: 768px) {
  .system-inner {
    grid-template-columns: 1fr 1fr;
  }
}

.system-visual {
  min-height: 280px;
}

.system-image {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 4/3;
  max-height: 360px;
  object-fit: contain;
  border-radius: 12px;
  border: none;
  outline: none;
}

.system-copy h2 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
}

.system-copy p {
  font-size: 1.125rem;
  line-height: 1.7;
  color: #333;
  margin-bottom: 1.25rem;
}

.link-arrow {
  font-size: 1rem;
  font-weight: 600;
  color: #2563eb;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.link-arrow:hover {
  text-decoration: underline;
}

.link-arrow-icon {
  transition: transform 0.2s ease;
}

.link-arrow:hover .link-arrow-icon {
  transform: translateX(3px);
}

.impact-section {
  padding: 24px 24px 48px;
  background: #fff;
}

.impact-inner {
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
}

.impact-inner h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.impact-list {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
  text-align: left;
  display: inline-block;
}

.impact-list li {
  font-size: 1.125rem;
  color: #333;
  padding: 0.35rem 0;
  position: relative;
  padding-left: 1.5rem;
}

.impact-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #2563eb;
  font-weight: 600;
}

.impact-close {
  font-size: 1.0625rem;
  color: #555;
  line-height: 1.6;
  margin: 0;
}

.cta-section {
  padding: 64px 24px;
  background: #0f172a;
  text-align: center;
}

.cta-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
}

.cta-section .btn {
  min-width: 160px;
}

.cta-section .btn-outline {
  background: transparent;
  color: #fff;
  border: 2px solid rgba(255,255,255,0.6);
}

.cta-section .btn-outline:hover {
  background: rgba(255,255,255,0.1);
  border-color: #fff;
}

.hero-content {
  max-width: 520px;
  margin: 0 auto;
  text-align: left;
}

.hero h1 {
  font-size: 1.875rem;
  font-weight: 600;
  line-height: 1.3;
  margin: 0 0 1rem 0;
  letter-spacing: -0.02em;
  color: #fff;
  text-shadow:
    1px 1px 0 #000,
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    0 1px 0 #000,
    0 -1px 0 #000,
    1px 0 0 #000,
    -1px 0 0 #000,
    0 0 4px #000;
}

.hero-sub {
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  opacity: 0.95;
  color: #fff;
  text-shadow:
    1px 1px 0 #000,
    -1px -1px 0 #000,
    1px -1px 0 #000,
    -1px 1px 0 #000,
    0 1px 0 #000,
    0 -1px 0 #000,
    1px 0 0 #000,
    -1px 0 0 #000,
    0 0 3px #000;
}

.hero-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  display: inline-block;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  transition: background-color 0.2s, color 0.2s;
}

.btn-primary {
  background-color: #2563eb;
  color: #fff;
}

.btn-primary:hover {
  background-color: #1d4ed8;
}

.btn-secondary {
  background-color: rgba(255,255,255,0.2);
  color: #fff;
  border: 2px solid rgba(255,255,255,0.8);
}

.btn-secondary:hover {
  background-color: rgba(255,255,255,0.3);
}

@media screen and (min-width: 768px) {
  .hero {
    min-height: 75vh;
    padding: 140px 48px 120px;
  }

  .hero-content {
    max-width: 640px;
  }

  .hero h1 {
    font-size: 3.5rem;
  }

  .hero-sub {
    font-size: 1.125rem;
  }
}

@media screen and (min-width: 1100px) {
  .hero-content {
    max-width: 860px;
    margin-left: 20%;
    margin-right: auto;
  }

  .hero h1 {
    font-size: 4.5rem;
  }

  .hero-sub {
    font-size: 1.35rem;
  }
}
</style>
